//dependencies
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { ObjectID } from "bson";
import React, { useEffect, useState } from "react";

import { faExclamationTriangle, faGripDotsVertical, faMinus, faPlus } from "@fortawesome/pro-regular-svg-icons";

import { ArrayFieldState, ButtonStandard, FormField, Icon, SubmitButton } from "@performyard/py-components";
import { SurveyModel, SurveyQuestionJson } from "@performyard/service-contracts";

//components
import InputTextArea from "../../../../../../../../components/Inputs/InputTextArea";
import SelectInput from "../../../../../../../../components/Inputs/SelectInput";

import { useEditingQuestion, useSetters } from "../../store/store";

//hooks
import { QuestionTypeMap, questionDisplayMap } from "../QuestionBlock";

const questionOptions: { value: string; label: string }[] = [
	{ value: "text", label: "Long Answer" },
	{ value: "multiple-choice", label: "Multiple Choice" },
	{ value: "multiple-select", label: "Multiple Choice" },
	{ value: "instructions", label: "Instructions" },
	{ value: "rating-scale", label: "Rating Scale" },
];

const filterByUniqueLabel = (options: { value: string; label: string }[]) => {
	const seen = new Set<string>();
	return options.filter((option) => {
		if (seen.has(option.label)) return false;
		seen.add(option.label);
		return true;
	});
};

const uniqueByLabel = filterByUniqueLabel(questionOptions);

type EditQuestionBlockProps = {
	data: QuestionTypeMap[keyof QuestionTypeMap];
	surveyStatus: SurveyModel["status"];
	publishedQuestion: boolean;
	field: ArrayFieldState<SurveyQuestionJson>;
	index: number;
};

const EditQuestionBlock = ({ data, surveyStatus, publishedQuestion, field, index }: EditQuestionBlockProps) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: data._id || "new",
	});
	const [isSubtitleToggled, setIsSubtitleToggled] = useState(false);

	// store
	const setters = useSetters();
	const storeEditingQuestion = useEditingQuestion();
	const editingQuestion = data;

	// Store the original question data when editing starts to restore on cancel
	const [originalQuestionData] = useState(() => ({ ...data }));

	const isNewestQuestion = storeEditingQuestion?.isNew;

	const style = {
		transform: CSS.Translate.toString(transform),
		transition,
	};

	const [ref, enableAnimation] = useAutoAnimate();

	useEffect(() => {
		if (isDragging) {
			enableAnimation(false);
		} else {
			setTimeout(() => {
				enableAnimation(false);
			}, 100);
		}
	}, [isDragging]);

	const handleToggleSubtext = (toggleSubtext: boolean) => {
		setIsSubtitleToggled(toggleSubtext);
		// Find the question by _id instead of using index to prevent affecting wrong question during drag and drop
		const questionIndex = field.value.findIndex((q) => q._id === editingQuestion._id);
		if (questionIndex !== -1) {
			field.replaceValue(questionIndex, { ...editingQuestion, subtitle: toggleSubtext ? "" : undefined });
		}
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			{...attributes}
			id={`question-${data?._id}`}
			className="mb-4 flex items-stretch justify-center border-[1px] border-solid border-neutral-2 bg-neutral-0"
		>
			<div
				{...listeners}
				className="flex w-6 flex-shrink-0 cursor-grab items-center justify-center border-r-[1px] border-solid border-neutral-2 hover:text-neutral-8 active:cursor-grabbing"
			>
				<Icon
					size={24}
					icon={faGripDotsVertical}
					color="neutral-7"
					dangerouslySetClassName="hover:text-neutral-9"
				/>
			</div>
			<div className="group max-w-full min-w-0 flex-shrink flex-grow break-words">
				<div className="flex items-center justify-between bg-neutral-1 pt-2 pr-4 pb-2 pl-4">
					<div className="w-fit">
						{(editingQuestion?.subtitle !== undefined && editingQuestion?.subtitle !== null) ||
						isSubtitleToggled ? (
							<ButtonStandard
								color="neutral"
								size="medium"
								iconLeft={faMinus}
								variant="outline"
								onClick={() => handleToggleSubtext(false)}
							>
								Remove Subtitle
							</ButtonStandard>
						) : (
							<ButtonStandard
								color="neutral"
								size="medium"
								iconLeft={faPlus}
								variant="outline"
								onClick={() => handleToggleSubtext(true)}
							>
								Add Subtitle
							</ButtonStandard>
						)}
					</div>
					<div className="flex gap-2">
						<ButtonStandard
							color="red"
							size="medium"
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();

								if (isNewestQuestion) {
									if (surveyStatus !== "draft" && field.value.length === 1) {
										setters.setBuilderBanner({
											message: "Published surveys require at least 1 question at all times.",
											color: "red",
											icon: faExclamationTriangle,
										});
									} else {
										// Find the question by _id instead of using index to prevent affecting wrong question during drag and drop
										const questionIndex = field.value.findIndex(
											(q) => q._id === editingQuestion._id
										);
										if (questionIndex !== -1) {
											// Remove from form field
											field.removeValue(questionIndex);
											// Also remove from store to prevent the question from being committed
											const currentQuestions = field.value.filter((_, i) => i !== questionIndex);
											setters.setOrderedQuestions(currentQuestions);
										}
									}
								} else {
									// Find the question by _id instead of using index to prevent affecting wrong question during drag and drop
									const questionIndex = field.value.findIndex((q) => q._id === editingQuestion._id);
									if (questionIndex !== -1) {
										// Restore the original question data to the form field
										field.replaceValue(questionIndex, originalQuestionData);
									}
								}

								setters.setInvalidQuestionMessages({});
								setters.setEditingQuestion(null);
							}}
						>
							Cancel
						</ButtonStandard>
						<SubmitButton
							color="primary"
							onSubmitMeta={{
								action: isNewestQuestion ? "create-question" : "update-question",
								questionIndex: index,
								_id: data._id,
							}}
						>
							{isNewestQuestion ? "Create" : "Update"}
						</SubmitButton>
					</div>
				</div>
				<div ref={ref} className="flex max-w-full min-w-0 flex-col gap-4 p-4">
					<div className="w-[186px]">
						<FormField<SurveyQuestionJson["type"]>
							id={`questions[${index}].type`}
							input={(typeField) => {
								return (
									<SelectInput
										menuPortalTarget={document.body}
										results={uniqueByLabel}
										onChange={(value) => {
											const newType = value?.value;

											// Handle options based on the new type
											let newOptions;
											if (newType === "multiple-choice" || newType === "multiple-select") {
												newOptions = [
													{
														_id: new ObjectID().toString(),
														text: "",
														order: 0,
													},
												];
											} else if (newType === "rating-scale") {
												newOptions = [
													{
														_id: new ObjectID().toString(),
														value: "",
														text: "",
														description: "",
														order: 0,
													},
												];
											} else {
												newOptions = undefined;
											}

											// Update the entire question with new type and options
											const updatedQuestion = {
												...editingQuestion,
												type: newType,
												options: newOptions,
											};
											// Find the question by _id instead of using index to prevent affecting wrong question during drag and drop
											const questionIndex = field.value.findIndex(
												(q) => q._id === editingQuestion._id
											);
											if (questionIndex !== -1) {
												field.replaceValue(questionIndex, updatedQuestion);
											}
										}}
										disabled={publishedQuestion}
										value={{
											value: typeField?.value,
											label: questionDisplayMap[typeField?.value || "text"]?.label,
										}}
									/>
								);
							}}
						/>
					</div>
					<FormField<string>
						id={`questions[${index}].text`}
						key={`${data._id}.text.${index}`}
						label={editingQuestion?.type === "instructions" ? "Instruction Title" : "Question Title"}
						validators={{
							onChangeListenTo: [`questions[${index}].subtitle`],
							onChange: ({ fieldApi }) => {
								fieldApi.state.meta.errors = [];
								fieldApi.state.meta.isTouched = true;
							},
						}}
						input={(field) => {
							return (
								<InputTextArea
									value={field?.value}
									onChange={(value) => field.onChange(value)}
									placeholder="Your question title"
									errored={field.errors.length > 0}
								/>
							);
						}}
					/>
					{((data?.subtitle !== undefined && data?.subtitle !== null) || isSubtitleToggled) && (
						<FormField<string>
							id={`questions[${index}].subtitle`}
							key={`${data._id}.subtitle.${index}`}
							label={
								editingQuestion?.type === "instructions" ? "Instruction Subtitle" : "Question Subtitle"
							}
							input={(field) => (
								<InputTextArea
									id={`questions[${index}].subtitle`}
									value={field.value}
									onChange={(value) => field.onChange(value)}
									placeholder="Your question subtitle"
									errored={field.errors.length > 0}
								/>
							)}
						/>
					)}
					{editingQuestion &&
						["multiple-choice", "multiple-select", "rating-scale"].includes(editingQuestion.type) &&
						questionDisplayMap[editingQuestion?.type]?.edit({
							data: editingQuestion,
							index,
							publishedQuestion,
							mutateField: (value) => {
								// Find the question by _id instead of using index to prevent affecting wrong question during drag and drop
								const questionIndex = field.value.findIndex((q) => q._id === editingQuestion._id);
								if (questionIndex !== -1) {
									field.replaceValue(questionIndex, { ...value });
								}
							},
						})}
					{editingQuestion &&
						["text", "instructions"].includes(editingQuestion.type) &&
						questionDisplayMap[editingQuestion?.type]?.edit({
							data: editingQuestion,
						})}
				</div>
			</div>
		</div>
	);
};

export default EditQuestionBlock;
